import { InfiniteScrollForm, type InfiniteScrollConfig } from "./infinite-scroll-form";
import { InfiniteScrollHandler } from "./infinite-scroll-handler";
import { InfiniteScrollListener } from "./infinite-scroll-listener";
import { InfiniteScrollRender } from "./infinite-scroll-render";

class InfiniteScrollController {
    private readonly form: InfiniteScrollForm;

    private readonly handler: InfiniteScrollHandler;

    private readonly listener: InfiniteScrollListener;

    public constructor(form: InfiniteScrollForm, handler: InfiniteScrollHandler, listener: InfiniteScrollListener) {
        this.form = form;
        this.handler = handler;
        this.listener = listener;
    }

    public bootstrap(enabled: boolean): void {
        this.handler.bootstrap();
        this.setEnabled(enabled);
    }

    public setEnabled(enabled: boolean): void {
        this.handler.setEnabled(enabled);

        if (!enabled) {
            this.listener.disconnect();
            return;
        }

        const nextUrl = this.form.getNextUrl();
        if (!nextUrl || this.handler.isDone) {
            this.listener.disconnect();
            return;
        }

        this.listener.register();
    }
}

export class InfiniteScrollSetup {
    private static readonly controllers = new Map<HTMLElement, InfiniteScrollController>();

    private static preferredEnabled = true;

    public static init(root: HTMLElement, config: InfiniteScrollConfig): void {
        if (!(root instanceof HTMLElement)) return;

        const existing = this.controllers.get(root);
        if (existing instanceof InfiniteScrollController) {
            existing.setEnabled(this.preferredEnabled);
            return;
        }

        const form = new InfiniteScrollForm(root, config);
        const render = new InfiniteScrollRender(form);
        const handler = new InfiniteScrollHandler(form, render);
        const listener = new InfiniteScrollListener(form, handler);
        const controller = new InfiniteScrollController(form, handler, listener);

        controller.bootstrap(this.preferredEnabled);

        this.controllers.set(root, controller);
        form.markInitialized();
    }

    public static setPreferredEnabled(enabled: boolean, root: ParentNode = document): void {
        this.preferredEnabled = enabled;

        this.controllers.forEach((controller, target) => {
            if (!root.contains(target)) return;
            controller.setEnabled(enabled);
        });
    }
}
